import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useDate } from './DateContext';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { authenticatedFetch } from '../services/authService';
import { socketService } from '@platform/socket';
import type { Notification as TopBarNotification } from '@ui';

export type Notification = TopBarNotification;

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchNotifications: (options?: FetchNotificationsOptions) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: (module?: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  createNotification: (notification: CreateNotificationData) => Promise<void>;
}

interface FetchNotificationsOptions {
  module?: string;
  read?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
  includeHistory?: boolean; // If true, load all notifications regardless of date
}

interface CreateNotificationData {
  userIds: number | number[];
  module: 'zamowienia' | 'sprzedaz' | 'kontroling' | 'global';
  type: 'chat' | 'system' | 'warning' | 'info';
  title: string;
  message: string;
  data?: any;
  link?: string;
  expiresAt?: string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  module?: 'zamowienia' | 'sprzedaz' | 'kontroling';
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, module }) => {
  const { user, isAuthenticated } = useAuth();
  const { selectedDate } = useDate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options: FetchNotificationsOptions = {}) => {
    console.log('[NotificationContext] fetchNotifications called', { selectedDate, isAuthenticated, user: !!user });
    if (!isAuthenticated || !user) {
      console.log('[NotificationContext] fetchNotifications skipped - not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (options.module || module) params.append('module', options.module || module || '');
      if (options.read !== undefined) params.append('read', String(options.read));
      if (options.type) params.append('type', options.type);
      if (options.limit) params.append('limit', String(options.limit));
      if (options.offset) params.append('offset', String(options.offset));
      // Add selected date from DateContext (always required, exact date filter)
      if (selectedDate) {
        params.append('date', selectedDate);
      }

      console.log('[NotificationContext] Fetching notifications with params:', params.toString());
      console.time('[NotificationContext] fetch duration');
      
      // Use setTimeout to allow React to complete its render cycle before fetching
      await new Promise(resolve => setTimeout(resolve, 10));
      console.log('[NotificationContext] After timeout, starting fetch...');
      
      let response;
      try {
        response = await authenticatedFetch(`/api/notifications/user?${params.toString()}`, {
          credentials: 'include'
        });
        console.log('[NotificationContext] Fetch completed, status:', response.status);
      } catch (fetchErr) {
        console.error('[NotificationContext] Fetch failed:', fetchErr);
        throw fetchErr;
      }
      console.timeEnd('[NotificationContext] fetch duration');

      if (!response.ok) {
        console.error('[NotificationContext] Response not OK:', response.status);
        throw new Error('Failed to fetch notifications');
      }

      console.log('[NotificationContext] Parsing JSON...');
      const data = await response.json();
      console.log('[NotificationContext] JSON parsed');
      
      if (data.success) {
        console.log('[NotificationContext] Notifications loaded:', { count: data.notifications?.length, unreadCount: data.unreadCount });
        console.log('[NotificationContext] Setting notifications state...');
        setNotifications(data.notifications);
        console.log('[NotificationContext] Setting unreadCount state...');
        setUnreadCount(data.unreadCount || 0);
        console.log('[NotificationContext] State updates completed');
      }
    } catch (err) {
      console.error('[NotificationContext] Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      console.log('[NotificationContext] Finally block - setting loading false');
      setLoading(false);
    }
  }, [isAuthenticated, user, module, selectedDate]);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    if (!isAuthenticated) return;

    try {
      const response = await authenticatedFetch(`/api/notifications/user/${id}/read`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [isAuthenticated]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (filterModule?: string) => {
    if (!isAuthenticated) return;

    try {
      const params = new URLSearchParams();
      if (filterModule || module) params.append('module', filterModule || module || '');

      const response = await authenticatedFetch(`/api/notifications/user/read-all?${params.toString()}`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => {
            if (!filterModule && !module) return { ...n, read: true };
            if (n.module === (filterModule || module)) return { ...n, read: true };
            return n;
          })
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [isAuthenticated, module]);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    if (!isAuthenticated) return;

    try {
      const response = await authenticatedFetch(`/api/notifications/user/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev => {
          const notification = prev.find(n => n.id === id);
          const wasUnread = notification && !notification.read;
          return prev.filter(n => n.id !== id);
        });
        
        // Update unread count if deleted notification was unread
        const wasUnread = notifications.find(n => n.id === id && !n.read);
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [isAuthenticated, notifications]);

  // Create notification (admin/system use)
  const createNotification = useCallback(async (notificationData: CreateNotificationData) => {
    if (!isAuthenticated) return;

    try {
      const response = await authenticatedFetch('/api/notifications/user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error('Failed to create notification');
      }

      const data = await response.json();
      
      if (data.success) {
        // Refresh notifications if this user is in the recipient list
        const userIdArray = Array.isArray(notificationData.userIds) 
          ? notificationData.userIds 
          : [notificationData.userIds];
        
        if (user && userIdArray.includes(user.id)) {
          await fetchNotifications();
        }
      }
    } catch (err) {
      console.error('Error creating notification:', err);
      throw err;
    }
  }, [isAuthenticated, user, selectedDate, fetchNotifications]);

  // Setup Socket.IO for real-time notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Użyj globalnego serwisu Socket.IO
    const initPromise = socketService.init();
    Promise.resolve(initPromise).catch(err => {
      console.error('[NotificationContext] Failed to initialize socket:', err);
    });

    const handleDbChange = (payload: any) => {
      try {
        // Check if this is a user_notifications event for current user
        if (payload.table === 'user_notifications' && payload.user_id === user.id) {
          console.log('[Notifications] Received notification event:', payload);
          
          if (payload.operation === 'INSERT') {
            // Add new notification to list
            const newNotification: Notification = {
              id: String(payload.id),
              userId: payload.user_id,
              module: payload.module,
              type: payload.type,
              title: payload.title,
              message: payload.message,
              read: payload.read || false,
              active: payload.active !== undefined ? payload.active : true,
              data: payload.data,
              link: payload.link,
              createdAt: payload.created_at
            };

            setNotifications(prev => [newNotification, ...prev]);
            
            // Only count active, unread notifications
            if (!newNotification.read && newNotification.active !== false) {
              setUnreadCount(prev => prev + 1);
            }

            // Show browser notification if permission granted
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(newNotification.title, {
                body: newNotification.message,
                icon: '/company_logo.gif',
                tag: newNotification.id
              });
            }
          } else if (payload.operation === 'UPDATE') {
            // Update existing notification
            setNotifications(prev =>
              prev.map(n =>
                n.id === String(payload.id)
                  ? {
                      ...n,
                      read: payload.read !== undefined ? payload.read : n.read,
                      active: payload.active !== undefined ? payload.active : n.active,
                      title: payload.title || n.title,
                      message: payload.message || n.message,
                      data: payload.data !== undefined ? payload.data : n.data
                    }
                  : n
              )
            );

            // Update unread count if read status or active status changed
            if (payload.read !== undefined || payload.active !== undefined) {
              const notification = notifications.find(n => n.id === String(payload.id));
              const wasActiveUnread = notification && !notification.read && notification.active !== false;
              const newActive = payload.active !== undefined ? payload.active : notification?.active;
              const newRead = payload.read !== undefined ? payload.read : notification?.read;
              const isActiveUnread = !newRead && newActive !== false;
              
              if (wasActiveUnread && !isActiveUnread) {
                setUnreadCount(prev => Math.max(0, prev - 1));
              } else if (!wasActiveUnread && isActiveUnread) {
                setUnreadCount(prev => prev + 1);
              }
            }
          } else if (payload.operation === 'DELETE') {
            // Remove notification from list
            const notification = notifications.find(n => n.id === String(payload.id));
            setNotifications(prev => prev.filter(n => n.id !== String(payload.id)));
            
            // Only decrement if it was active and unread
            if (notification && !notification.read && notification.active !== false) {
              setUnreadCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      } catch (err) {
        console.error('[Notifications] Error processing socket event:', err);
      }
    };

    // Subskrybuj notyfikacje przez globalny serwis
    const unsubscribe = socketService.subscribe(handleDbChange);

    return () => {
      unsubscribe();
      // Nie zamykaj socketu - jest współdzielony przez cały moduł
    };
  }, [isAuthenticated, user]);

  // Initial fetch of notifications (only after authentication is complete)
  // Load notifications for selected date (exact date filter)
  // This will also reload when selectedDate changes (because fetchNotifications depends on selectedDate)
  useAuthenticatedEffect(() => {
    console.log('[NotificationContext] useAuthenticatedEffect triggered, fetching notifications for date:', selectedDate);
    fetchNotifications({ limit: 100 });
  }, [fetchNotifications]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('[Notifications] Browser notification permission:', permission);
      });
    }
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

